import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import HeroSlider from "../components/HeroSlider";
import NewsletterSignup from "../components/NewsletterSignup";
import { api } from "../lib/api";
import { useLang } from "../lib/lang";
import { Calendar, ArrowUpRight, MapPin } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function HomePage() {
  const { t, tri } = useLang();
  const [sliders, setSliders] = useState([]);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/sliders").then((r) => setSliders(r.data)).catch(() => {});
    api.get("/news?limit=6").then((r) => setNews(r.data)).catch(() => {});
    api.get("/events?upcoming=true").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="App" data-testid="home-page">
      <Header />
      <HeroSlider slides={sliders} />

      {/* Latest News */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="home-news-section">
        <div className="flex items-end justify-between mb-10 border-b border-slate-200 pb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-red-600 font-semibold mb-2">
              {t("hero_section")}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl text-slate-900">{t("latest_news")}</h2>
          </div>
          <Link to="/news" className="hidden md:flex items-center gap-2 text-sm uppercase tracking-[0.2em] font-semibold text-slate-700 hover:text-red-600 transition" data-testid="home-news-viewall">
            {t("view_all")} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        {news.length === 0 ? (
          <div className="text-slate-500" data-testid="home-news-empty">{t("no_data")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.map((n, i) => (
              <Link
                to={`/news/${n.id}`}
                key={n.id}
                data-testid={`home-news-${i}`}
                className="kn-card group bg-white border border-slate-200"
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  {n.image_url && (
                    <img
                      src={n.image_url}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] mb-3">
                    <span className="text-red-600 font-semibold">{n.category}</span>
                    <span className="text-slate-500">{fmtDate(n.created_at)}</span>
                  </div>
                  <h3 className="font-display text-2xl text-slate-900 leading-tight mb-3">
                    {tri(n.title)}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-3">{tri(n.excerpt)}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] font-semibold text-slate-900 group-hover:text-red-600 transition">
                    {t("read_more")} <ArrowUpRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Events */}
      {events.length > 0 && (
        <section className="bg-slate-900 text-white py-20" data-testid="home-events-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-10 border-b border-white/10 pb-6">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-red-500 font-semibold mb-2">Briefings</div>
                <h2 className="font-display text-4xl sm:text-5xl">{t("upcoming_events")}</h2>
              </div>
              <Link to="/events" className="hidden md:flex items-center gap-2 text-sm uppercase tracking-[0.2em] font-semibold hover:text-red-500 transition" data-testid="home-events-viewall">
                {t("view_all")} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {events.map((e, i) => (
                <div key={e.id} className="border border-white/15 p-6 hover:border-red-500 transition" data-testid={`home-event-${i}`}>
                  <div className="text-xs uppercase tracking-[0.3em] text-red-500 mb-3">
                    <Calendar className="inline w-3 h-3 mr-1" /> {fmtDate(e.event_date)}
                  </div>
                  <h3 className="font-display text-2xl mb-3 leading-tight">{tri(e.title)}</h3>
                  {e.location && (
                    <div className="text-xs text-slate-400 mb-3 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {e.location}
                    </div>
                  )}
                  <p className="text-sm text-slate-300 line-clamp-3">{tri(e.description)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <NewsletterSignup variant="light" />

      <Footer />
    </div>
  );
}
